import React, { useMemo, useState, useEffect, useRef } from "react";
import moment from "moment";
import { FiEye, FiEdit, FiTrash2 } from "react-icons/fi";
import { toast } from "react-toastify";
import ViewApplicantModal from "./ViewApplicantModal";
import EditApplicantModal from "./EditApplicantModal";
import axios from "axios";
import * as XLSX from "xlsx";       // Excel/CSV
import { saveAs } from "file-saver"; 
import jsPDF from "jspdf";          // PDF
import autoTable from "jspdf-autotable";

const API_URL = "https://ims.holdthechild.net/api";

export default function ApplicantsTable({ applicants, jobs, loading, onRefresh }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [jobFilter, setJobFilter] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [applicantToDelete, setApplicantToDelete] = useState(null);
  
  const itemsPerPage = 10;
  const token = localStorage.getItem("token");

  // --- EXPORT DROPDOWN STATE ---
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Create a mapping of job IDs to job details for easy lookup
  const jobMap = useMemo(() => {
    const map = {};
    jobs.forEach(job => {
      map[job._id] = job;
    });
    return map;
  }, [jobs]);

  // Helper function to get the job name for an applicant
const getJobName = (applicant) => {
  // Check if jobId is populated with the job object
  if (applicant.jobId && typeof applicant.jobId === 'object' && applicant.jobId.vacancy) {
    return applicant.jobId.vacancy;
  }
  // If jobId is just an ID string, look it up in our map
  if (applicant.jobId && typeof applicant.jobId === 'string' && jobMap[applicant.jobId]) {
    return jobMap[applicant.jobId].vacancy;
  }
  // Fallback for data integrity issues
  return "Job not specified";
};
  const normalizeStatus = (s = "") => s.toUpperCase();

    const filteredApplicants = useMemo(() => {
    return applicants.filter((a) => {
      const jobName = getJobName(a); // Use helper for consistent lookup
      const matchesSearch =
        a.fullName?.toLowerCase().includes(search.toLowerCase()) ||
        a.email?.toLowerCase().includes(search.toLowerCase()) ||
        jobName.toLowerCase().includes(search.toLowerCase());

      const matchesStatus =
        statusFilter === "ALL" ||
        normalizeStatus(a.status) === statusFilter;
        
      // FIX: Compare the ID of the job object (a.jobId._id) to the filter string (jobFilter)
      const matchesJob =
        jobFilter === "ALL" ||
        (a.jobId && a.jobId._id === jobFilter);

      return matchesSearch && matchesStatus && matchesJob;
    });
  }, [applicants, search, statusFilter, jobFilter, jobMap]);
  const totalPages = Math.ceil(filteredApplicants.length / itemsPerPage);

  const paginatedApplicants = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredApplicants.slice(start, start + itemsPerPage);
  }, [filteredApplicants, currentPage]);
  // --- EXPORT FUNCTIONS ---
  const mapApplicantToExportRow = (a) => ({
    Name: a.fullName,
    Email: a.email,
    Job: getJobName(a),
    Status: a.status,
    AppliedOn: moment(a.createdAt).format("YYYY-MM-DD"),
  });

  const exportToExcel = () => {
    if (!filteredApplicants.length) return toast.info("No applicants to export");
    const data = filteredApplicants.map(mapApplicantToExportRow);
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Applicants");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "applicants.xlsx");
    setExportDropdownOpen(false);
  };

  const exportToCSV = () => {
    if (!filteredApplicants.length) return toast.info("No applicants to export");
    const data = filteredApplicants.map(mapApplicantToExportRow);
    const worksheet = XLSX.utils.json_to_sheet(data);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    saveAs(new Blob([csv], { type: "text/csv;charset=utf-8;" }), "applicants.csv");
    setExportDropdownOpen(false);
  };

  const exportToPDF = () => {
    if (!filteredApplicants.length) return toast.info("No applicants to export");
    const data = filteredApplicants.map(mapApplicantToExportRow);
    const doc = new jsPDF();
    autoTable(doc, {
      head: [["Name", "Email", "Job", "Status", "Applied On"]],
      body: data.map(row => Object.values(row)),
    });
    doc.save("applicants.pdf");
    setExportDropdownOpen(false);
  };

  const handleExport = (format) => {
    switch (format) {
      case "excel": exportToExcel(); break;
      case "csv": exportToCSV(); break;
      case "pdf": exportToPDF(); break;
      default: break;
    }
  };

  
  // --- UPDATED DELETE HANDLERS ---
  const handleDelete = (id) => {
    // Instead of confirming immediately, we open the modal and store the ID
    setApplicantToDelete(id);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    // This function now performs the actual deletion
    if (!applicantToDelete) return;
    
    // Close the modal immediately for better UX
    setDeleteModalOpen(false); 

    try {
      await axios.delete(`${API_URL}/applicants/${applicantToDelete}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Show a clear, user-friendly success message
      toast.success("Applicant deleted successfully!");
      onRefresh();
    } catch (err) {
      // Show a clear, user-friendly error message
      toast.error("Failed to delete applicant. Please try again.");
    } finally {
      // Clear the state after the operation
      setApplicantToDelete(null);
    }
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setApplicantToDelete(null);
  };

  return (
    
    <div className="bg-white rounded shadow p-4 mt-6">
      <h2 className="text-lg font-semibold mb-4">Applicants Table</h2>

      {/* Filters + Export Row */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <input
            type="search"
            placeholder="Search applicants"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border rounded px-3 py-2 w-full sm:w-auto"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border rounded px-3 py-2"
          >
            <option value="ALL">All Statuses</option>
            <option value="SHORTLISTED">Shortlisted</option>
            <option value="SUCCESSFUL">Successful</option>
            <option value="UNSUCCESSFUL">Unsuccessful</option>
          </select>
          <select
            value={jobFilter}
            onChange={(e) => setJobFilter(e.target.value)}
            className="border rounded px-3 py-2"
          >
            <option value="ALL">All Jobs</option>
            {jobs.map((job) => (
              <option key={job._id} value={job._id}>{job.vacancy}</option>
            ))}
          </select>
        </div>

        {/* Export Button on the right */}
        <div className="relative inline-block text-left" ref={dropdownRef}>
          <button
            onClick={() => setExportDropdownOpen((open) => !open)}
            className="inline-flex justify-center rounded bg-orange-600 px-4 py-2 text-white hover:bg-orange-700"
          >
            Export
            <svg className="ml-2 -mr-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd"/>
            </svg>
          </button>
          {exportDropdownOpen && (
            <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-50">
              <div className="py-1">
                {["excel","csv","pdf"].map((format) => (
                  <button
                    key={format}
                    onClick={() => handleExport(format)}
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-orange-100 hover:text-orange-900"
                  >
                    Export as {format.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
        

      {loading ? (
        <p className="text-center py-6">Loading applicants...</p>
      ) : (
        <div className="overflow-x-auto">
  <table className="min-w-full text-sm">
    <thead className="bg-orange-100 text-orange-500">

      <tr>
        {/* Added align-middle and text alignment classes */}
        <th className="px-4 py-2 text-left align-middle">Name</th>
        <th className="px-4 py-2 text-left align-middle">Job Applied</th>
        <th className="px-4 py-2 text-left align-middle">Source</th>
        <th className="px-4 py-2 text-left align-middle">Status</th>
        <th className="px-4 py-2 text-center align-middle">Applied On</th>
        <th className="px-4 py-2 text-center align-middle">Action</th>
      </tr>
    </thead>
    <tbody>
      {paginatedApplicants.length ? (
        paginatedApplicants.map((a) => (
          <tr key={a._id} className="border-b">
            {/* Added align-middle and text alignment classes to each td */}
            <td className="px-4 py-2 text-left align-middle">{a.fullName}</td>
            <td className="px-4 py-2 text-left align-middle">
              {getJobName(a)}
            </td>
            <td className="px-4 py-2 text-left align-middle">{a.source}</td>
            <td className="px-4 py-2 text-left align-middle text-orange-600">{a.status}</td>
            <td className="px-4 py-2 text-center align-middle">
              {moment(a.createdAt).format("MMM D, YYYY")}
            </td>
            <td className="px-4 py-2 text-center align-middle">
              {/* 
                We wrap the buttons in a div and use flex with justify-center
                to perfectly center the icons within the cell.
              */}
              <div className="flex gap-2 justify-center">
                <button onClick={() => { setSelectedApplicant(a); setViewOpen(true); }}>
                  <FiEye />
                </button>
                <button onClick={() => { setSelectedApplicant(a); setEditOpen(true); }}>
                  <FiEdit className="text-orange-600" />
                </button>
                <button onClick={() => handleDelete(a._id)}>
                  <FiTrash2 className="text-red-600" />
                </button>
              </div>
            </td>
          </tr>
        ))
      ) : (
        <tr>
          <td colSpan="6" className="text-center py-6 text-gray-500">
            No applicants found
          </td>
        </tr>
      )}
    </tbody>
  </table>
</div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={`px-3 py-1 rounded ${
                currentPage === i + 1
                  ? "bg-orange-300"
                  : "bg-gray-200"
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
      {/* --- CUSTOM DELETE CONFIRMATION MODAL --- */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm mx-4 shadow-xl">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
              Confirm Deletion
            </h3>
            <p className="text-sm text-center text-gray-600 mb-6">
              Are you sure you want to delete this applicant? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={closeDeleteModal}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {viewOpen && (
        <ViewApplicantModal
          applicant={selectedApplicant}
          onClose={() => setViewOpen(false)}
        />
      )}

      {editOpen && (
        <EditApplicantModal
          applicant={selectedApplicant}
          onClose={() => setEditOpen(false)}
          onUpdated={onRefresh}
        />
      )}
    </div>
  );
}
